import { useCallback, useEffect, useRef, useState } from 'react'
import { supabase } from '../lib/supabaseClient'
import { useAuth } from '../contexts/AuthContext'
import { getPartnerId } from '../lib/owner'

// Couple chat thread over direct_messages. RLS already scopes every
// row to (sender = me OR recipient = me), and the trusted pair is
// just owner ↔ guest, so "the thread" is simply every row ordered by
// created_at. Realtime keeps it live: INSERT for new bubbles, UPDATE
// for read receipts + edits.
//
// Scheduled messages (scheduled_for in the future, dispatched_at
// null) are filtered to the *sender* only so a pending send shows in
// their own timeline with a clock badge but doesn't leak to the
// recipient until the cron dispatches it.
export function useChatThread(partnerIdArg) {
  const { user } = useAuth()
  const userId = user?.id || null
  // Partner is the route's :partnerId when provided, falling back to
  // the trusted-pair partner. Scoping to a specific counterpart keeps
  // the thread correct if the contact list ever grows past the pair.
  const partnerId = partnerIdArg || getPartnerId(user)

  const [messages, setMessages] = useState([])
  // reactionsByMessage: { [messageId]: [{ user_id, emoji }] }
  const [reactionsByMessage, setReactionsByMessage] = useState({})
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const seen = useRef(new Set())

  const belongsToThread = useCallback((row) => {
    if (!partnerId) return false
    return (
      (row.sender_id === userId && row.recipient_id === partnerId)
      || (row.sender_id === partnerId && row.recipient_id === userId)
    )
  }, [userId, partnerId])

  const visibleToMe = useCallback((row) => {
    // Hide other people's not-yet-dispatched scheduled rows. Mine
    // (sender = me) stay visible so I can see what's queued.
    if (row.dispatched_at) return true
    if (!row.scheduled_for) return true
    return row.sender_id === userId
  }, [userId])

  const refetch = useCallback(async () => {
    if (!userId) return
    const { data } = await supabase
      .from('direct_messages')
      .select('id, sender_id, recipient_id, title, body, image_url, audio_url, image_view_limit, image_expired, reply_to_id, scheduled_for, dispatched_at, push_sent, push_failed, created_at, read_at, edited_at, deleted_at')
      .order('created_at', { ascending: true })
      .limit(500)
    const rows = (data || []).filter((r) => belongsToThread(r) && visibleToMe(r))
    seen.current = new Set(rows.map((r) => r.id))
    setMessages(rows)

    // Reactions for everything in the thread. RLS only returns rows
    // on messages the viewer is part of, so an unfiltered pull is
    // safe and cheap for a two-person chat.
    const { data: reactions } = await supabase
      .from('direct_message_reactions')
      .select('message_id, user_id, emoji')
    const map = {}
    for (const r of reactions || []) {
      ;(map[r.message_id] ||= []).push({ user_id: r.user_id, emoji: r.emoji })
    }
    setReactionsByMessage(map)
  }, [userId, visibleToMe])

  useEffect(() => {
    if (!userId) {
      setMessages([])
      setLoading(false)
      return undefined
    }
    let alive = true
    setLoading(true)
    const channel = supabase
      .channel(`dm-thread-${userId}-${Math.random().toString(36).slice(2)}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'direct_messages' },
        async () => { if (alive) await refetch() },
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'direct_message_reactions' },
        async () => { if (alive) await refetch() },
      )
      .subscribe()
    refetch().then(() => { if (alive) setLoading(false) })
    return () => { alive = false; supabase.removeChannel(channel) }
  }, [userId, refetch])

  // Mark every inbound unread message read. Called when the thread is
  // on screen. One update covers the batch.
  const markRead = useCallback(async () => {
    if (!userId) return
    const unreadIds = messages
      .filter((m) => m.recipient_id === userId && !m.read_at)
      .map((m) => m.id)
    if (!unreadIds.length) return
    await supabase
      .from('direct_messages')
      .update({ read_at: new Date().toISOString() })
      .in('id', unreadIds)
  }, [userId, messages])

  // Send a chat bubble. `extra` carries optional image_url / audio_url
  // / reply_to_id / title. Persists the row immediately (optimistic),
  // then fires the push via the edge function so the partner gets a
  // notification too. Returns the inserted row id or null.
  const send = useCallback(async ({ body = '', title = null, replyToId = null, imageUrl = null, audioUrl = null, imageViewLimit = null, scheduledFor = null } = {}) => {
    if (!userId || !partnerId) return null
    const text = (body || '').trim()
    if (!text && !imageUrl && !audioUrl) return null
    const isScheduled = scheduledFor && new Date(scheduledFor).getTime() > Date.now()

    // Optimistic bubble — render immediately so the chat feels instant
    // instead of waiting on the network. The real row replaces it on
    // the next refetch (matched away by id); on failure we flag it.
    const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2)}`
    if (!isScheduled) {
      const optimistic = {
        id: tempId, sender_id: userId, recipient_id: partnerId,
        title: title || null, body: text || null, image_url: imageUrl,
        audio_url: audioUrl, image_view_limit: imageViewLimit, image_expired: false,
        reply_to_id: replyToId,
        scheduled_for: null, dispatched_at: new Date().toISOString(),
        push_sent: null, push_failed: null,
        created_at: new Date().toISOString(), read_at: null, _pending: true,
      }
      setMessages((prev) => [...prev, optimistic])
    }

    setSending(true)
    try {
      // Scheduled send: insert the row directly (RLS lets the sender
      // insert) with scheduled_for set + dispatched_at null. The
      // dispatch-scheduled-messages cron fires the push when due.
      if (isScheduled) {
        const { data, error } = await supabase
          .from('direct_messages')
          .insert({
            sender_id: userId,
            recipient_id: partnerId,
            title: title || null,
            body: text || null,
            reply_to_id: replyToId,
            image_url: imageUrl,
            audio_url: audioUrl,
            scheduled_for: new Date(scheduledFor).toISOString(),
          })
          .select('id')
          .single()
        if (error) return null
        await refetch()
        return data?.id || null
      }

      const resp = await supabase.functions.invoke('send-custom-push', {
        body: {
          recipient_user_id: partnerId,
          title: title || '',
          body: text,
          reply_to_id: replyToId,
          image_url: imageUrl,
          audio_url: audioUrl,
          image_view_limit: imageViewLimit,
        },
      })
      if (resp.error) {
        // Mark the optimistic bubble failed instead of silently dropping.
        setMessages((prev) => prev.map((m) => (m.id === tempId ? { ...m, _pending: false, _failed: true } : m)))
        return null
      }
      const id = resp.data?.message_id || null
      await refetch()
      return id
    } finally {
      setSending(false)
    }
  }, [userId, partnerId, refetch])

  // Toggle a reaction on a message. Same emoji again = remove it;
  // a different emoji = replace (the unique(message_id,user_id)
  // constraint enforces one reaction per person, so we upsert on
  // that conflict target).
  const react = useCallback(async (messageId, emoji) => {
    if (!userId || !messageId || !emoji) return
    const mine = (reactionsByMessage[messageId] || []).find((r) => r.user_id === userId)
    if (mine && mine.emoji === emoji) {
      await supabase
        .from('direct_message_reactions')
        .delete()
        .eq('message_id', messageId)
        .eq('user_id', userId)
    } else {
      await supabase
        .from('direct_message_reactions')
        .upsert(
          { message_id: messageId, user_id: userId, emoji },
          { onConflict: 'message_id,user_id' },
        )
    }
    await refetch()
  }, [userId, reactionsByMessage, refetch])

  // Re-fire the push for a message whose delivery failed. Doesn't
  // create a new row — the edge function looks the message up by id
  // and re-sends to the recipient's current subscriptions.
  const resendPush = useCallback(async (messageId) => {
    if (!messageId) return
    await supabase.functions.invoke('send-custom-push', {
      body: { resend_message_id: messageId },
    })
    await refetch()
  }, [refetch])

  // Count a view of an ephemeral image. The caller must have already
  // loaded the image bytes — once the limit's hit the server deletes
  // the file and nulls the url (realtime UPDATE re-renders the bubble
  // as "seen").
  const consumeViewOnce = useCallback(async (messageId) => {
    if (!messageId) return
    await supabase.functions.invoke('consume-view-once-image', { body: { message_id: messageId } })
    await refetch()
  }, [refetch])

  // Edit own message body. Stamps edited_at; realtime UPDATE re-renders.
  const editMessage = useCallback(async (messageId, newBody) => {
    if (!messageId) return
    const text = (newBody || '').trim()
    if (!text) return
    await supabase
      .from('direct_messages')
      .update({ body: text, edited_at: new Date().toISOString() })
      .eq('id', messageId)
    await refetch()
  }, [refetch])

  // Unsend (soft-delete) own message — the bubble becomes "deleted".
  const unsendMessage = useCallback(async (messageId) => {
    if (!messageId) return
    await supabase
      .from('direct_messages')
      .update({ deleted_at: new Date().toISOString(), body: null, image_url: null, audio_url: null })
      .eq('id', messageId)
    await refetch()
  }, [refetch])

  return { messages, reactionsByMessage, loading, sending, send, react, resendPush, consumeViewOnce, editMessage, unsendMessage, markRead, refetch, userId, partnerId }
}
